<script setup>
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useSupplierBrandColumn } from '@/composables/data-grid-column'
import { useRegistryStore } from '@/stores/registry.store'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const props = defineProps({
  subject: {
    type: Object,
    required: true
  },
  entityLock: {
    type: Object,
    required: true
  },
})
const subjectRefId = props.subject.id
const supplierId = props.subject.supplier_ref
const gridId = `supplier-division-grid_${supplierId}`

const i18n = getI18n()
const registryStore = useRegistryStore()

const { gridRef } = dataGrid.useGridRef()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.supplierDivisionsUrl)
gridDataSource.filter(['supplier_ref', '=', supplierId])

const rowButtons = props.entityLock.getEntityLockButtons()

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  { dataField: 'code', caption: i18n.t('common.code'), validationRules: [{ type: 'required' }] },
  {
    dataField: 'name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
  },
  {
    dataField: 'supplier_division_type_ref',
    caption: i18n.t('common.type'),
    lookup: dataGrid.useLookup({ dataSource: registryStore.getLocalizedSupplierDivisionTypes() }),
    validationRules: [{ type: 'required' }],
  },
  useSupplierBrandColumn(),
]

const { defaultActionTitle, defaultItemTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.division')}`
  if (i.id) {
    title += ` ${i.code}`
  }
  const defaultItemTitleValue = defaultItemTitle(props.templateData?.data)
  if (defaultItemTitleValue) {
    title += ` ${defaultItemTitleValue}`
  }
  return title
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 4,
      items: [
        { dataField: 'code' },
        { dataField: 'name' },
        { dataField: 'supplier_brand_ref' },
        { dataField: 'supplier_division_type_ref' },
      ]
    },
  ]
}, {
  allowAdding: supplierId < 0,
  allowUpdating: supplierId < 0 && !props.entityLock?.isLocked(false, subjectRefId),
  allowDeleting: supplierId < 0 && !props.entityLock?.isLocked(false, subjectRefId),
  onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
})

const onInitNewRow = (e) => {
  e.data.supplier = supplierId
  e.promise = registryStore.api.getSupplierDivisionNextIdAsync().then(res => { e.data.code = res.data })
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow: onInitNewRow,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {}),
  height: '100%',
})

</script>
<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
  >
    <template
      #editButtonsTemplate="{ data: cellInfo }"
    >
      <edit-lock-buttons-template
        :entity-name="'Subject'"
        :master-entity-id="subjectRefId"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :buttons="rowButtons"
      />
    </template>
    <template #customToolbarAddButtonTemplate>
      <custom-toolbar-add-button-template
        :master-entity-id="subjectRefId"
        :grid="gridRef"
        :entity-lock="props.entityLock"
      />
    </template>
  </dx-data-grid>
</template>
<style>
</style>
